import type {
  MemoryRetrievalScope,
  PersistentCyanMemoryRecord,
  PersistentMemoryRepository,
} from "./persistentMemoryRepository";

export type GroundedMemoryStrategy =
  "LEXICAL" | "RECENT_HISTORY" | "HYBRID" | "NONE";

export type GroundedMemoryResult = {
  records: PersistentCyanMemoryRecord[];

  strategy: GroundedMemoryStrategy;

  grounded: boolean;
};

function normalizeText(value: string): string {
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function looksReferential(query: string): boolean {
  const normalized = normalizeText(query);

  if (!normalized) {
    return false;
  }

  const tokens = normalized.split(" ");

  /*
   * Referential questions are often short and contain
   * discourse/history concepts rather than subject terms.
   *
   * This is deliberately multilingual but not intended to be
   * an exhaustive language model. It only decides whether to
   * activate bounded recent-history fallback.
   */
  const referenceTokens = new Set([
    "again",
    "before",
    "earlier",
    "previous",
    "previously",
    "remember",
    "chat",
    "conversation",

    "novamente",
    "antes",
    "anterior",
    "anteriores",
    "lembra",
    "lembrar",
    "chat",
    "conversa",

    "otra",
    "otro",
    "antes",
    "anterior",
    "recuerdas",
    "conversacion",

    "avant",
    "precedent",
    "souviens",
    "conversation",
  ]);

  const hasReferenceToken = tokens.some((token) => referenceTokens.has(token));

  const lowInformation = tokens.length <= 12;

  return hasReferenceToken && lowInformation;
}

function deduplicate(
  records: PersistentCyanMemoryRecord[],
): PersistentCyanMemoryRecord[] {
  const seen = new Set<string>();

  const output: PersistentCyanMemoryRecord[] = [];

  for (const record of records) {
    const key = record.id || `${record.source.type}:${record.source.id}`;

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    output.push(record);
  }

  return output;
}

function assertGrounded(records: PersistentCyanMemoryRecord[]): void {
  const grounded = records.every((record) =>
    Boolean(record.source.type && record.source.id),
  );

  if (!grounded) {
    throw new Error("Retrieved Cyan memory contains ungrounded records.");
  }
}

export class GroundedMemoryRetrievalService {
  constructor(private readonly repository: PersistentMemoryRepository) {}

  async retrieve(
    scope: MemoryRetrievalScope,
    query: string,
    limit = 20,
  ): Promise<GroundedMemoryResult> {
    const normalized = query.trim();

    if (!normalized) {
      return {
        records: [],
        strategy: "NONE",
        grounded: true,
      };
    }

    const safeLimit = Math.max(1, Math.min(limit, 100));

    const lexical = await this.repository.retrieve(
      scope,
      normalized,
      safeLimit,
    );

    assertGrounded(lexical);

    const referential = looksReferential(normalized);

    if (!referential) {
      return {
        records: lexical.slice(0, safeLimit),

        strategy: lexical.length ? "LEXICAL" : "NONE",

        grounded: true,
      };
    }

    const recent = await this.repository.listRecent(scope, safeLimit);

    assertGrounded(recent);

    const records = deduplicate([...lexical, ...recent]).slice(0, safeLimit);

    let strategy: GroundedMemoryStrategy;

    if (lexical.length && recent.length) {
      strategy = "HYBRID";
    } else if (lexical.length) {
      strategy = "LEXICAL";
    } else if (recent.length) {
      strategy = "RECENT_HISTORY";
    } else {
      strategy = "NONE";
    }

    return {
      records,
      strategy,
      grounded: true,
    };
  }
}
